import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Crown, ArrowLeft, Zap, MessageCircle, Infinity, ShieldCheck } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import Logo from "../components/Logo";

const FEATURES = [
    { icon: Infinity, title: "Unlimited Swipes", desc: "Free users get 20/day. You? All you want." },
    { icon: MessageCircle, title: "Message Anyone You Swipe", desc: "Skip the mutual-match wait. Right swipe = instant chat." },
    { icon: Zap, title: "Priority Placement", desc: "Your profile shows up first in their lineup." },
    { icon: ShieldCheck, title: "Premium Badge", desc: "Visible crown on your profile. It reads." },
];

export default function Premium() {
    const nav = useNavigate();
    const { user, refresh } = useAuth();
    const [status, setStatus] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/billing/status");
                setStatus(data);
            } catch {}
        })();
    }, []);

    const subscribe = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/billing/checkout", {
                plan_id: "smash_plus_monthly",
                origin_url: window.location.origin,
            });
            window.location.href = data.url;
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not start checkout");
            setBusy(false);
        }
    };

    const isPremium = status?.premium || user?.premium;

    return (
        <div className="min-h-screen flex flex-col text-white px-6 pt-6 pb-10 relative overflow-hidden">
            <div className="pointer-events-none absolute -top-40 -left-40 w-[500px] h-[500px] bg-dash/15 blur-[120px] rounded-full" />
            <div className="pointer-events-none absolute -bottom-40 -right-40 w-[500px] h-[500px] bg-smash/20 blur-[120px] rounded-full" />

            <button
                onClick={() => nav(-1)}
                data-testid="premium-back-btn"
                className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-white/50 hover:text-white w-fit relative z-10"
            >
                <ArrowLeft className="w-3.5 h-3.5" /> Back
            </button>

            <div className="relative z-10 mt-4">
                <Logo size="sm" />
            </div>

            <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="relative z-10 mt-8"
            >
                <div className="inline-flex items-center gap-2 border border-dash/70 bg-dash/10 px-3 py-1 mb-5">
                    <Crown className="w-3.5 h-3.5 text-dash" />
                    <span className="text-[10px] uppercase tracking-[0.3em] text-dash font-semibold">Smash+ Premium</span>
                </div>
                <h1 className="font-display text-5xl sm:text-6xl leading-[0.95] mb-3">
                    SWIPE<br /><span className="text-smash">WITHOUT</span><br />LIMITS.
                </h1>
                <p className="text-white/70 text-sm max-w-sm">
                    $15/month. Unlimited swipes, message anyone you swipe, priority placement, premium badge.
                </p>
            </motion.div>

            {isPremium ? (
                <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="relative z-10 mt-8 border-2 border-dash bg-dash/10 p-5"
                    data-testid="premium-active-card"
                >
                    <div className="flex items-center gap-2 mb-2">
                        <Crown className="w-5 h-5 text-dash" />
                        <h2 className="font-display text-2xl text-dash">YOU ARE SMASH+.</h2>
                    </div>
                    <p className="text-sm text-white/80">
                        Your premium is active
                        {status?.premium_until && (
                            <> until <span className="text-dash">{new Date(status.premium_until).toLocaleDateString()}</span></>
                        )}
                        . Go crush it.
                    </p>
                    <button
                        onClick={() => nav("/discover")}
                        data-testid="premium-go-swiping-btn"
                        className="mt-4 w-full h-12 bg-dash hover:bg-dash/90 text-black font-display text-sm uppercase tracking-widest"
                    >
                        Start Swiping
                    </button>
                </motion.div>
            ) : (
                <>
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.15 }}
                        className="relative z-10 mt-8 space-y-3"
                        data-testid="premium-features-list"
                    >
                        {FEATURES.map(({ icon: Icon, title, desc }, i) => (
                            <div key={title} className="flex items-start gap-3 border border-white/10 bg-ink-800/60 p-4">
                                <div className="w-10 h-10 bg-smash/20 border border-smash/40 flex items-center justify-center shrink-0">
                                    <Icon className="w-5 h-5 text-smash" />
                                </div>
                                <div>
                                    <div className="font-display text-base uppercase tracking-wider text-white">{title}</div>
                                    <div className="text-xs text-white/60 mt-0.5">{desc}</div>
                                </div>
                                <Check className="w-4 h-4 text-dash ml-auto shrink-0 mt-1" />
                            </div>
                        ))}
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.25 }}
                        className="relative z-10 mt-8"
                    >
                        <div className="flex items-baseline gap-2 mb-3">
                            <span className="font-display text-5xl text-smash">$15</span>
                            <span className="text-sm text-white/60 uppercase tracking-widest">/month</span>
                        </div>
                        <button
                            onClick={subscribe}
                            disabled={busy}
                            data-testid="premium-subscribe-btn"
                            className="w-full h-14 bg-smash hover:bg-smash/90 disabled:opacity-50 text-white font-display text-lg tracking-wider uppercase transition-colors animate-pulse-danger"
                        >
                            {busy ? "Redirecting…" : "Go Smash+"}
                        </button>
                        <p className="text-center text-[10px] text-white/40 mt-3 uppercase tracking-widest">
                            Secure checkout via Stripe · Cancel anytime
                        </p>
                    </motion.div>
                </>
            )}
        </div>
    );
}
